import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useMyCourses } from "@/hooks/use-my-courses";
import { CourseCard, pickCat } from "./CourseCard";

const CATEGORY_LABELS: Record<string, string> = {
  onboarding: "Onboarding",
  tutoring: "Tutoring Standards",
  franchise: "Franchise Standards",
  hr: "HR & Compliance",
  operations: "Standard Operations",
  marketing: "Promotions & Marketing",
  customer: "Customer Service",
  technology: "Technology & Systems",
  delivery: "Course Delivery",
};

const labelFor = (c: unknown) => {
  const s = String(c ?? "");
  return CATEGORY_LABELS[s] || (s ? s.charAt(0).toUpperCase() + s.slice(1) : "General");
};

export default function CoursesScreen() {
  const { data: courses = [], isLoading } = useMyCourses();
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState<string>("all");

  const categories = useMemo(() => {
    const set = new Set<string>();
    courses.forEach((c) => set.add(c.category));
    return Array.from(set).sort();
  }, [courses]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return courses.filter((c) => {
      if (activeCat !== "all" && c.category !== activeCat) return false;
      if (q && !c.title.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [courses, search, activeCat]);

  return (
    <div className="px-5 pt-12 pb-4">
      <h1 className="text-2xl font-extrabold text-[#2A1810]">Courses</h1>

      {/* Search */}
      <div className="relative mt-4">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A6050]"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search courses"
          className="w-full rounded-full border border-[#F0DDD2] bg-[#F4E5DA] py-2.5 pl-9 pr-4 text-sm text-[#2A1810] placeholder:text-[#8A6050] focus:outline-none focus:ring-2 focus:ring-[#F44321]/40"
        />
      </div>

      {/* Chips */}
      <div className="-mx-5 mt-4 flex gap-2 overflow-x-auto px-5 pb-1 [&::-webkit-scrollbar]:hidden">
        {["all", ...categories].map((c) => {
          const active = activeCat === c;
          return (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              className="flex-none rounded-full border px-3 py-1.5 text-xs font-bold transition-colors"
              style={
                active
                  ? { background: "#F44321", color: "#FFFFFF", borderColor: "#F44321" }
                  : { background: "#FFF6EC", color: "#5A4035", borderColor: "#F0DDD2" }
              }
            >
              {c === "all" ? "All" : labelFor(c)}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="mt-4 flex flex-col gap-3">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-[76px] w-full animate-pulse rounded-[14px] border border-[#F0DDD2] bg-[#FFF6EC]"
            />
          ))
        ) : filtered.length === 0 ? (
          <p className="mt-10 text-center text-sm font-medium text-[#8A6050]">
            No courses yet.
          </p>
        ) : (
          filtered.map((c) => (
            <CourseCard
              key={c.id}
              id={c.id}
              title={c.title}
              category={labelFor(c.category)}
              cat={pickCat(c.category)}
              progress={c.progress}
              completed={c.completed}
              coverUrl={c.cover_image}
            />
          ))
        )}
      </div>
    </div>
  );
}
